/**
 * Tic-Tac-Logic Validation
 *
 * Validates the three core rules:
 * 1. No more than two consecutive X's or O's
 * 2. Equal number of X's and O's in each row/column
 * 3. All rows and columns must be unique
 */

import { type CellValue, type GameState, type ValidationError, CellFlags, EMPTY } from './types';
import {
  getRow,
  getColumn,
  getIndex,
  hasThreeConsecutive,
  findThreeConsecutive,
  countSymbols,
  linesIdentical,
  isLineComplete,
} from './grid';

/**
 * Check if a row has a triple violation
 */
export function hasRowTriple(cells: CellValue[], size: number, row: number): boolean {
  return hasThreeConsecutive(getRow(cells, size, row));
}

/**
 * Check if a column has a triple violation
 */
export function hasColumnTriple(cells: CellValue[], size: number, col: number): boolean {
  return hasThreeConsecutive(getColumn(cells, size, col));
}

/**
 * Check if any row/column has a triple violation
 */
export function hasTripleViolation(cells: CellValue[], size: number): boolean {
  for (let i = 0; i < size; i++) {
    if (hasRowTriple(cells, size, i) || hasColumnTriple(cells, size, i)) {
      return true;
    }
  }
  return false;
}

/**
 * Check if a row has a count violation (too many of one symbol)
 */
export function hasRowCountViolation(cells: CellValue[], size: number, row: number): boolean {
  const maxPerLine = size / 2;
  const counts = countSymbols(getRow(cells, size, row));
  return counts.x > maxPerLine || counts.o > maxPerLine;
}

/**
 * Check if a column has a count violation
 */
export function hasColumnCountViolation(cells: CellValue[], size: number, col: number): boolean {
  const maxPerLine = size / 2;
  const counts = countSymbols(getColumn(cells, size, col));
  return counts.x > maxPerLine || counts.o > maxPerLine;
}

/**
 * Check if any row/column has a count violation
 */
export function hasCountViolation(cells: CellValue[], size: number): boolean {
  for (let i = 0; i < size; i++) {
    if (hasRowCountViolation(cells, size, i) || hasColumnCountViolation(cells, size, i)) {
      return true;
    }
  }
  return false;
}

/**
 * Find duplicate rows (only checks complete rows)
 */
export function findDuplicateRows(cells: CellValue[], size: number): number[][] {
  const duplicates: number[][] = [];
  const rows = [];

  for (let i = 0; i < size; i++) {
    rows.push(getRow(cells, size, i));
  }

  for (let i = 0; i < size; i++) {
    if (!isLineComplete(rows[i]!)) continue;

    for (let j = i + 1; j < size; j++) {
      if (linesIdentical(rows[i]!, rows[j]!)) {
        // Check if we already have a group with row i
        let found = false;
        for (const group of duplicates) {
          if (group.includes(i)) {
            if (!group.includes(j)) group.push(j);
            found = true;
            break;
          }
        }
        if (!found) {
          duplicates.push([i, j]);
        }
      }
    }
  }

  return duplicates;
}

/**
 * Find duplicate columns (only checks complete columns)
 */
export function findDuplicateColumns(cells: CellValue[], size: number): number[][] {
  const duplicates: number[][] = [];
  const cols = [];

  for (let i = 0; i < size; i++) {
    cols.push(getColumn(cells, size, i));
  }

  for (let i = 0; i < size; i++) {
    if (!isLineComplete(cols[i]!)) continue;

    for (let j = i + 1; j < size; j++) {
      if (linesIdentical(cols[i]!, cols[j]!)) {
        let found = false;
        for (const group of duplicates) {
          if (group.includes(i)) {
            if (!group.includes(j)) group.push(j);
            found = true;
            break;
          }
        }
        if (!found) {
          duplicates.push([i, j]);
        }
      }
    }
  }

  return duplicates;
}

/**
 * Check if there are any duplicate rows or columns
 */
export function hasDuplicateViolation(cells: CellValue[], size: number): boolean {
  return findDuplicateRows(cells, size).length > 0 || findDuplicateColumns(cells, size).length > 0;
}

/**
 * Check if current grid state has any violations
 */
export function hasAnyViolation(cells: CellValue[], size: number): boolean {
  return (
    hasTripleViolation(cells, size) || hasCountViolation(cells, size) || hasDuplicateViolation(cells, size)
  );
}

/**
 * Check if a specific cell placement is valid
 */
export function isValidPlacement(cells: CellValue[], size: number, x: number, y: number): boolean {
  // Check row and column for triple violation
  if (hasRowTriple(cells, size, y) || hasColumnTriple(cells, size, x)) {
    return false;
  }

  // Check count violations
  if (hasRowCountViolation(cells, size, y) || hasColumnCountViolation(cells, size, x)) {
    return false;
  }

  // Check duplicate violations (only if row/column is complete)
  const row = getRow(cells, size, y);
  const col = getColumn(cells, size, x);

  if (isLineComplete(row)) {
    for (let i = 0; i < size; i++) {
      if (i !== y && linesIdentical(row, getRow(cells, size, i))) {
        return false;
      }
    }
  }

  if (isLineComplete(col)) {
    for (let i = 0; i < size; i++) {
      if (i !== x && linesIdentical(col, getColumn(cells, size, i))) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Check if puzzle is completely and correctly solved
 */
export function isSolved(cells: CellValue[], size: number): boolean {
  // Check all cells are filled
  if (cells.some((c) => c === EMPTY)) {
    return false;
  }

  // Check no violations
  return !hasAnyViolation(cells, size);
}

/**
 * Get all validation errors for display
 */
export function getValidationErrors(cells: CellValue[], size: number): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check rows for triples
  for (let y = 0; y < size; y++) {
    const row = getRow(cells, size, y);
    const triplePositions = findThreeConsecutive(row);
    if (triplePositions.length > 0) {
      errors.push({
        type: 'triple',
        direction: 'row',
        index: y,
        cells: triplePositions.map((x) => ({ x, y })),
      });
    }
  }

  // Check columns for triples
  for (let x = 0; x < size; x++) {
    const col = getColumn(cells, size, x);
    const triplePositions = findThreeConsecutive(col);
    if (triplePositions.length > 0) {
      errors.push({
        type: 'triple',
        direction: 'column',
        index: x,
        cells: triplePositions.map((y) => ({ x, y })),
      });
    }
  }

  // Check rows for count violations
  for (let y = 0; y < size; y++) {
    if (hasRowCountViolation(cells, size, y)) {
      const row = getRow(cells, size, y);
      errors.push({
        type: 'count',
        direction: 'row',
        index: y,
        cells: row.map((_, x) => ({ x, y })),
      });
    }
  }

  // Check columns for count violations
  for (let x = 0; x < size; x++) {
    if (hasColumnCountViolation(cells, size, x)) {
      errors.push({
        type: 'count',
        direction: 'column',
        index: x,
        cells: Array.from({ length: size }, (_, y) => ({ x, y })),
      });
    }
  }

  // Check for duplicate rows
  const dupRows = findDuplicateRows(cells, size);
  for (const group of dupRows) {
    for (const y of group) {
      errors.push({
        type: 'duplicate',
        direction: 'row',
        index: y,
        cells: Array.from({ length: size }, (_, x) => ({ x, y })),
      });
    }
  }

  // Check for duplicate columns
  const dupCols = findDuplicateColumns(cells, size);
  for (const group of dupCols) {
    for (const x of group) {
      errors.push({
        type: 'duplicate',
        direction: 'column',
        index: x,
        cells: Array.from({ length: size }, (_, y) => ({ x, y })),
      });
    }
  }

  return errors;
}

/**
 * Update error flags in game state
 */
export function updateErrorFlags(state: GameState): void {
  const { cells, flags, size } = state;

  // Clear all error flags
  for (let i = 0; i < flags.length; i++) {
    flags[i] =
      (flags[i] ?? 0) &
      ~(
        CellFlags.ERROR |
        CellFlags.ERROR_ROW_TRIPLE |
        CellFlags.ERROR_COL_TRIPLE |
        CellFlags.ERROR_ROW_COUNT |
        CellFlags.ERROR_COL_COUNT |
        CellFlags.ERROR_ROW_DUPLICATE |
        CellFlags.ERROR_COL_DUPLICATE
      );
  }

  const errors = getValidationErrors(cells, size);

  for (const error of errors) {
    let errorFlag = CellFlags.ERROR;
    if (error.type === 'triple') {
      errorFlag |= error.direction === 'row' ? CellFlags.ERROR_ROW_TRIPLE : CellFlags.ERROR_COL_TRIPLE;
    } else if (error.type === 'count') {
      errorFlag |= error.direction === 'row' ? CellFlags.ERROR_ROW_COUNT : CellFlags.ERROR_COL_COUNT;
    } else if (error.type === 'duplicate') {
      errorFlag |= error.direction === 'row' ? CellFlags.ERROR_ROW_DUPLICATE : CellFlags.ERROR_COL_DUPLICATE;
    }

    for (const cell of error.cells) {
      const idx = getIndex(size, cell.x, cell.y);
      flags[idx] = (flags[idx] ?? 0) | errorFlag;
    }
  }
}

/**
 * Check if a cell has an error
 */
export function hasError(state: GameState, x: number, y: number): boolean {
  const idx = getIndex(state.size, x, y);
  return ((state.flags[idx] ?? 0) & CellFlags.ERROR) !== 0;
}

/**
 * Check if a cell is immutable (given)
 */
export function isImmutable(state: GameState, x: number, y: number): boolean {
  const idx = getIndex(state.size, x, y);
  return ((state.flags[idx] ?? 0) & CellFlags.IMMUTABLE) !== 0;
}

/**
 * Check if puzzle is complete (all cells filled correctly)
 */
export function checkComplete(state: GameState): boolean {
  if (isSolved(state.cells, state.size)) {
    state.completed = true;
    return true;
  }
  return false;
}
